/**
 * Imperative toast. Anywhere in the app:
 *   toast('Saved.', 'success');
 *   toast('Could not save.', 'danger');
 *
 * Mount <ToastStack /> once at the app shell. Toasts slide in from the
 * top-left with a small pop, hold for `ms` milliseconds (default 4000),
 * then fade out. They can also be dismissed with the × button.
 */

import { useEffect, useState } from 'react';
import { Icon } from './Icon';

export type ToastTone = 'info' | 'success' | 'danger';

interface Item {
  id: number;
  message: string;
  tone: ToastTone;
  leaving: boolean;
}

const ENTER_LINGER_MS = 0;
const EXIT_MS = 220;
const DEFAULT_DURATION = 4000;

let counter = 0;
let stack: Item[] = [];
const listeners = new Set<(items: Item[]) => void>();

function emit(): void { listeners.forEach((cb) => cb(stack)); }

function startLeaving(id: number): void {
  stack = stack.map((i) => i.id === id ? { ...i, leaving: true } : i);
  emit();
  setTimeout(() => {
    stack = stack.filter((i) => i.id !== id);
    emit();
  }, EXIT_MS);
}

export function toast(message: string, tone: ToastTone = 'info', ms: number = DEFAULT_DURATION): number {
  const id = ++counter;
  stack = [...stack, { id, message, tone, leaving: false }];
  emit();
  // Hold, then trigger the leaving animation.
  window.setTimeout(() => startLeaving(id), ms + ENTER_LINGER_MS);
  return id;
}

export function dismissToast(id: number): void { startLeaving(id); }

export function ToastStack() {
  const [items, setItems] = useState<Item[]>(stack);
  useEffect(() => {
    const cb = (next: Item[]): void => setItems(next);
    listeners.add(cb);
    return () => { listeners.delete(cb); };
  }, []);

  return (
    <div className="toast-stack" role="region" aria-label="Notifications" aria-live="polite">
      {items.map((i) => (
        <div key={i.id} className={`toast tone-${i.tone}${i.leaving ? ' leaving' : ''}`} role="status">
          <span className="ico" aria-hidden>
            {i.tone === 'success'
              ? <Icon name="check" size={14} />
              : i.tone === 'danger' ? '!' : 'i'}
          </span>
          <span className="msg">{i.message}</span>
          <button
            type="button"
            className="close"
            aria-label="Dismiss notification"
            onClick={() => dismissToast(i.id)}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
